import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  CloudDownload as ImportIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  DeleteOutline as DeleteIcon,
  Search as SearchIcon,
  Science as ScienceIcon,
  CheckCircleOutline as ActiveIcon,
  RemoveCircleOutline as InactiveIcon,
  Save as SaveIcon,
  Sync as SyncIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import {
  referenceMaterialsService,
  ReferenceMaterial,
  ReferenceMaterialValue,
  OreasScrapeLog,
} from '../../services/referenceMaterialsService';

/**
 * Phase 1 — Admin "OREAS / MRC catalog" management surface.
 *
 *  - Lists every reference material visible to the tenant (global OREAS rows
 *    + tenant-private custom MRCs).
 *  - "Importer depuis OREAS" runs a live scrape against oreas.com via the
 *    backend service and previews the analyte table before saving.
 *  - "Ajouter MRC manuellement" creates an in-house / non-OREAS MRC.
 *  - Click a row → analyte editor drawer (inline value edits + per-row delete).
 */

const PRODUCERS = ['OREAS', 'AMIS', 'GEOSTATS', 'CUSTOM'];

const ReferenceMaterialsManagement: React.FC = () => {
  const [materials, setMaterials] = useState<ReferenceMaterial[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [producerFilter, setProducerFilter] = useState<string>('');
  const [activeFilter, setActiveFilter] = useState<'all' | 'active' | 'inactive'>('all');

  // Import dialog
  const [importOpen, setImportOpen] = useState(false);
  const [importCode, setImportCode] = useState('');
  const [importTenantScoped, setImportTenantScoped] = useState(false);
  const [importBusy, setImportBusy] = useState(false);
  const [importPreview, setImportPreview] = useState<ReferenceMaterial | null>(null);
  const [importError, setImportError] = useState<string | null>(null);

  // Phase 4.3 — bulk OREAS sync
  const [syncOpen, setSyncOpen] = useState(false);
  const [syncBusy, setSyncBusy] = useState(false);
  const [syncLogs, setSyncLogs] = useState<OreasScrapeLog[]>([]);
  const [syncError, setSyncError] = useState<string | null>(null);

  // Manual create dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [createDraft, setCreateDraft] = useState<Partial<ReferenceMaterial> & { values: ReferenceMaterialValue[] }>({
    code: '',
    producer: 'CUSTOM',
    name: '',
    matrix: '',
    is_active: true,
    values: [],
  });

  // Edit drawer (analyte values)
  const [editTarget, setEditTarget] = useState<ReferenceMaterial | null>(null);
  const [editValues, setEditValues] = useState<ReferenceMaterialValue[]>([]);
  const [editBusy, setEditBusy] = useState(false);

  // OREAS catalog autocomplete (lazy-loaded once dialog opens).
  const [oreasCatalog, setOreasCatalog] = useState<string[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(false);

  const reload = async () => {
    try {
      setLoading(true);
      setError(null);
      const list = await referenceMaterialsService.list();
      setMaterials(list);
    } catch (e: any) {
      console.error(e);
      setError(e?.message || 'Impossible de charger le catalogue');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
  }, []);

  const visibleMaterials = useMemo(() => {
    return materials.filter(rm => {
      if (producerFilter && rm.producer !== producerFilter) return false;
      if (activeFilter === 'active' && !rm.is_active) return false;
      if (activeFilter === 'inactive' && rm.is_active) return false;
      if (search) {
        const needle = search.toLowerCase();
        const haystack = [rm.code, rm.name, rm.matrix, rm.mineralisation, rm.description]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!haystack.includes(needle)) return false;
      }
      return true;
    });
  }, [materials, producerFilter, activeFilter, search]);

  // ---------------- Import dialog ----------------
  const [catalogError, setCatalogError] = useState<string | null>(null);

  const ensureCatalogLoaded = async () => {
    if (oreasCatalog.length > 0 || catalogLoading) return;
    try {
      setCatalogLoading(true);
      setCatalogError(null);
      const res = await referenceMaterialsService.listOreasCatalog();
      setOreasCatalog(res.codes || []);
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Impossible de charger la liste OREAS';
      console.warn('Could not preload OREAS catalog index', err);
      setCatalogError(msg);
    } finally {
      setCatalogLoading(false);
    }
  };

  const handleImport = async () => {
    if (!importCode.trim()) return;
    setImportBusy(true);
    setImportError(null);
    setImportPreview(null);
    try {
      const result = await referenceMaterialsService.importFromOreas(
        importCode.trim(),
        importTenantScoped,
      );
      setImportPreview(result);
      await reload();
    } catch (e: any) {
      setImportError(e?.response?.data?.message || e?.message || 'Import OREAS échoué');
    } finally {
      setImportBusy(false);
    }
  };

  // ---------------- Manual create ----------------
  const resetCreateDraft = () =>
    setCreateDraft({
      code: '',
      producer: 'CUSTOM',
      name: '',
      matrix: '',
      is_active: true,
      values: [],
    });

  const addCreateRow = () =>
    setCreateDraft(d => ({
      ...d,
      values: [
        ...(d.values || []),
        {
          analyte_symbol: '',
          analyte_name: '',
          unit: 'wt.%',
          method: '4-Acid Digestion',
          certified_value: null,
          sd_1: null,
        },
      ],
    }));

  const handleManualCreate = async () => {
    if (!createDraft.code) return;
    try {
      await referenceMaterialsService.create({
        code: createDraft.code,
        producer: createDraft.producer || 'CUSTOM',
        name: createDraft.name,
        matrix: createDraft.matrix,
        is_active: createDraft.is_active ?? true,
        values: createDraft.values,
      } as any);
      setCreateOpen(false);
      resetCreateDraft();
      await reload();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Création échouée');
    }
  };

  // ---------------- Value editor ----------------
  const openEditor = async (rm: ReferenceMaterial) => {
    setEditTarget(rm);
    try {
      const fresh = await referenceMaterialsService.findOne(rm.id);
      setEditValues(fresh.values || []);
    } catch {
      setEditValues(rm.values || []);
    }
  };

  const updateEditValue = (idx: number, patch: Partial<ReferenceMaterialValue>) =>
    setEditValues(rows => rows.map((r, i) => (i === idx ? { ...r, ...patch } : r)));

  const addEditRow = () =>
    setEditValues(rows => [
      ...rows,
      {
        analyte_symbol: '',
        analyte_name: '',
        unit: 'wt.%',
        method: '4-Acid Digestion',
        certified_value: null,
        sd_1: null,
      },
    ]);

  const persistEditValues = async () => {
    if (!editTarget) return;
    setEditBusy(true);
    try {
      await referenceMaterialsService.upsertValues(
        editTarget.id,
        editValues.filter(v => v.analyte_symbol && v.method),
      );
      await reload();
      const refreshed = await referenceMaterialsService.findOne(editTarget.id);
      setEditTarget(refreshed);
      setEditValues(refreshed.values || []);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Échec de la sauvegarde');
    } finally {
      setEditBusy(false);
    }
  };

  const removeEditRow = async (row: ReferenceMaterialValue, idx: number) => {
    if (row.id && editTarget) {
      try {
        await referenceMaterialsService.deleteValue(editTarget.id, row.id);
      } catch (e: any) {
        setError(e?.response?.data?.message || 'Échec de la suppression');
        return;
      }
    }
    setEditValues(rows => rows.filter((_, i) => i !== idx));
  };

  const softDelete = async (rm: ReferenceMaterial) => {
    if (!window.confirm(`Désactiver le MRC "${rm.code}" ?`)) return;
    try {
      await referenceMaterialsService.softDelete(rm.id);
      await reload();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Échec de la désactivation');
    }
  };

  // ---------------- Render ----------------
  return (
    <Box>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        alignItems={{ md: 'center' }}
        justifyContent="space-between"
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Catalogue MRC / OREAS
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Matériaux de référence certifiés utilisés en QA/QC. Importez les valeurs
            depuis oreas.com ou ajoutez vos MRC internes.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            variant="contained"
            startIcon={<ImportIcon />}
            onClick={() => {
              setImportOpen(true);
              setImportPreview(null);
              setImportError(null);
              ensureCatalogLoaded();
            }}
          >
            Importer depuis OREAS
          </Button>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => setCreateOpen(true)}
          >
            Ajouter MRC manuellement
          </Button>
          <Button
            variant="outlined"
            color="info"
            startIcon={<SyncIcon />}
            onClick={async () => {
              setSyncOpen(true);
              setSyncError(null);
              try {
                const logs = await referenceMaterialsService.listOreasSyncLogs(30);
                setSyncLogs(logs);
              } catch (e: any) {
                setSyncError(e?.message || 'Erreur de chargement des journaux');
              }
            }}
          >
            Synchroniser OREAS
          </Button>
          <Tooltip title="Recharger">
            <IconButton onClick={reload}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={5}>
            <TextField
              fullWidth
              size="small"
              placeholder="Recherche (code, matrice, description…)"
              value={search}
              onChange={e => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <FormControl size="small" fullWidth>
              <InputLabel>Producteur</InputLabel>
              <Select
                value={producerFilter}
                label="Producteur"
                onChange={e => setProducerFilter(e.target.value as string)}
              >
                <MenuItem value="">Tous</MenuItem>
                {PRODUCERS.map(p => (
                  <MenuItem key={p} value={p}>
                    {p}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={3}>
            <FormControl size="small" fullWidth>
              <InputLabel>Statut</InputLabel>
              <Select
                value={activeFilter}
                label="Statut"
                onChange={e => setActiveFilter(e.target.value as any)}
              >
                <MenuItem value="all">Tous</MenuItem>
                <MenuItem value="active">Actifs</MenuItem>
                <MenuItem value="inactive">Inactifs</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Code</TableCell>
              <TableCell>Producteur</TableCell>
              <TableCell>Matrice</TableCell>
              <TableCell align="right"># Analytes</TableCell>
              <TableCell>Statut</TableCell>
              <TableCell>Dernière sync.</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            )}
            {!loading && visibleMaterials.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                  <Typography color="text.secondary">
                    Aucun MRC dans le catalogue. Importez OREAS 415 pour démarrer.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {!loading &&
              visibleMaterials.map(rm => (
                <TableRow
                  key={rm.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => openEditor(rm)}
                >
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <ScienceIcon fontSize="small" color="action" />
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {rm.code}
                        </Typography>
                        {rm.name && (
                          <Typography variant="caption" color="text.secondary">
                            {rm.name}
                          </Typography>
                        )}
                      </Box>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={rm.producer} />
                  </TableCell>
                  <TableCell>{rm.matrix || '—'}</TableCell>
                  <TableCell align="right">{rm.values?.length ?? 0}</TableCell>
                  <TableCell>
                    {rm.is_active ? (
                      <Chip
                        size="small"
                        color="success"
                        icon={<ActiveIcon />}
                        label="Actif"
                      />
                    ) : (
                      <Chip
                        size="small"
                        color="default"
                        icon={<InactiveIcon />}
                        label="Inactif"
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    {rm.last_synced_at ? new Date(rm.last_synced_at).toLocaleString() : '—'}
                  </TableCell>
                  <TableCell align="right" onClick={e => e.stopPropagation()}>
                    <Tooltip title="Modifier les analytes">
                      <IconButton size="small" onClick={() => openEditor(rm)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Désactiver">
                      <IconButton size="small" onClick={() => softDelete(rm)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ---------------- Import dialog ---------------- */}
      <Dialog open={importOpen} onClose={() => setImportOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Importer un MRC depuis OREAS</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Alert severity="info">
              Saisissez le numéro OREAS (ex. 415, OREAS 22i, 908). Le système ouvre la
              fiche correspondante sur <code>oreas.com</code>, parse la table des
              valeurs certifiées et l'enregistre dans le catalogue.
            </Alert>
            {catalogError && (
              <Alert severity="warning" sx={{ py: 0.5 }}>
                {catalogError} — vous pouvez quand même saisir le code manuellement.
              </Alert>
            )}
            <Autocomplete
              freeSolo
              loading={catalogLoading}
              options={oreasCatalog}
              value={importCode}
              onInputChange={(_, v) => setImportCode(v)}
              renderInput={params => (
                <TextField
                  {...params}
                  label="Numéro OREAS"
                  placeholder="ex. 415"
                  helperText={catalogLoading ? 'Chargement de la liste…' : undefined}
                />
              )}
            />
            <Stack direction="row" alignItems="center" spacing={2}>
              <Switch
                checked={importTenantScoped}
                onChange={(_, v) => setImportTenantScoped(v)}
              />
              <Box>
                <Typography variant="body2">Privé à mon tenant</Typography>
                <Typography variant="caption" color="text.secondary">
                  Décochez pour partager les valeurs scrapées avec tous les tenants
                  (recommandé pour OREAS).
                </Typography>
              </Box>
            </Stack>
            {importError && <Alert severity="error">{importError}</Alert>}
            {importPreview && (
              <Box>
                <Alert severity="success" sx={{ mb: 1 }}>
                  Importé : {importPreview.code} — {importPreview.values?.length ?? 0}{' '}
                  analytes enregistrés.
                </Alert>
                <TableContainer component={Paper} sx={{ maxHeight: 360 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Analyte</TableCell>
                        <TableCell>Méthode</TableCell>
                        <TableCell align="right">Valeur</TableCell>
                        <TableCell align="right">1SD</TableCell>
                        <TableCell>Unité</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(importPreview.values || []).map(v => (
                        <TableRow key={v.id || `${v.analyte_symbol}-${v.method}`}>
                          <TableCell>
                            {v.analyte_symbol}
                            {v.analyte_name ? ` — ${v.analyte_name}` : ''}
                          </TableCell>
                          <TableCell>{v.method}</TableCell>
                          <TableCell align="right">
                            {v.is_indicative
                              ? `< ${v.certified_value ?? ''}`
                              : v.certified_value ?? '—'}
                          </TableCell>
                          <TableCell align="right">{v.sd_1 ?? '—'}</TableCell>
                          <TableCell>{v.unit}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportOpen(false)}>Fermer</Button>
          <Button
            variant="contained"
            startIcon={importBusy ? <CircularProgress size={16} /> : <ImportIcon />}
            disabled={importBusy || !importCode.trim()}
            onClick={handleImport}
          >
            {importBusy ? 'Import en cours…' : 'Lancer l’import'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---------------- Manual create dialog ---------------- */}
      <Dialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Ajouter un MRC manuellement</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Code"
                value={createDraft.code || ''}
                onChange={e => setCreateDraft(d => ({ ...d, code: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl size="small" fullWidth>
                <InputLabel>Producteur</InputLabel>
                <Select
                  label="Producteur"
                  value={createDraft.producer || 'CUSTOM'}
                  onChange={e =>
                    setCreateDraft(d => ({ ...d, producer: e.target.value as string }))
                  }
                >
                  {PRODUCERS.map(p => (
                    <MenuItem key={p} value={p}>
                      {p}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Nom"
                value={createDraft.name || ''}
                onChange={e => setCreateDraft(d => ({ ...d, name: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Matrice"
                value={createDraft.matrix || ''}
                onChange={e => setCreateDraft(d => ({ ...d, matrix: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mt: 1, mb: 1 }}
              >
                <Typography variant="subtitle2">Analytes certifiés</Typography>
                <Button size="small" startIcon={<AddIcon />} onClick={addCreateRow}>
                  Ajouter une ligne
                </Button>
              </Stack>
              <ValueRowsEditor
                rows={createDraft.values || []}
                onChange={rows => setCreateDraft(d => ({ ...d, values: rows }))}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Annuler</Button>
          <Button
            variant="contained"
            disabled={!createDraft.code}
            onClick={handleManualCreate}
          >
            Créer
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---------------- Value editor drawer (modal) ---------------- */}
      <Dialog
        open={!!editTarget}
        onClose={() => setEditTarget(null)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          {editTarget?.code} — Analytes certifiés
          {editTarget?.matrix && (
            <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
              ({editTarget.matrix})
            </Typography>
          )}
        </DialogTitle>
        <DialogContent dividers>
          <Stack
            direction="row"
            spacing={1}
            justifyContent="flex-end"
            sx={{ mb: 1 }}
          >
            <Button size="small" startIcon={<AddIcon />} onClick={addEditRow}>
              Ajouter une ligne
            </Button>
          </Stack>
          <ValueRowsEditor
            rows={editValues}
            onChange={setEditValues}
            onDeleteRow={removeEditRow}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditTarget(null)}>Fermer</Button>
          <Button
            variant="contained"
            startIcon={editBusy ? <CircularProgress size={16} /> : <SaveIcon />}
            disabled={editBusy}
            onClick={persistEditValues}
          >
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Phase 4.3 — OREAS bulk-sync dialog */}
      <Dialog
        open={syncOpen}
        onClose={() => setSyncOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" spacing={1} alignItems="center">
            <HistoryIcon />
            <span>Synchronisation OREAS</span>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Lance une re-vérification de tous les MRC OREAS déjà présents dans
            le catalogue local. Les valeurs certifiées sont rafraîchies et
            l'audit ci-dessous trace ce qui a changé. Une exécution
            automatique est programmée le 1er de chaque mois à 03h15.
          </Typography>

          {syncError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {syncError}
            </Alert>
          )}

          <Button
            variant="contained"
            color="primary"
            startIcon={syncBusy ? <CircularProgress size={16} /> : <SyncIcon />}
            disabled={syncBusy}
            onClick={async () => {
              setSyncBusy(true);
              setSyncError(null);
              try {
                const res = await referenceMaterialsService.runOreasSync();
                setSyncLogs((prev) => [res.log, ...prev]);
                await reload();
              } catch (e: any) {
                setSyncError(e?.message || 'Erreur lors de la synchronisation');
              } finally {
                setSyncBusy(false);
              }
            }}
          >
            Lancer maintenant
          </Button>

          <TableContainer component={Paper} sx={{ mt: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Déclencheur</TableCell>
                  <TableCell align="right">Total</TableCell>
                  <TableCell align="right">Ajoutés</TableCell>
                  <TableCell align="right">Modifiés</TableCell>
                  <TableCell align="right">Inchangés</TableCell>
                  <TableCell align="right">Échecs</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {syncLogs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography variant="body2" color="text.secondary">
                        Aucune exécution enregistrée.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  syncLogs.map((log) => (
                    <TableRow key={log.id} hover>
                      <TableCell>{new Date(log.started_at).toLocaleString('fr-FR')}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={log.trigger}
                          color={log.trigger === 'manual' ? 'primary' : 'default'}
                        />
                      </TableCell>
                      <TableCell align="right">{log.total_codes}</TableCell>
                      <TableCell align="right">
                        <Typography color="success.main" component="span">
                          {log.added_codes}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography color="warning.main" component="span">
                          {log.updated_codes}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">{log.unchanged_codes}</TableCell>
                      <TableCell align="right">
                        <Typography color={log.failed_codes ? 'error.main' : 'text.secondary'} component="span">
                          {log.failed_codes}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSyncOpen(false)}>Fermer</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

interface ValueRowsEditorProps {
  rows: ReferenceMaterialValue[];
  onChange: (rows: ReferenceMaterialValue[]) => void;
  onDeleteRow?: (row: ReferenceMaterialValue, idx: number) => void;
}

// Memoized single row: only re-renders when its own data/index/callbacks change.
// This keeps editing a single field from re-rendering the entire (potentially
// 150+ row) analyte table — the previous behaviour that froze the browser.
interface ValueRowProps {
  row: ReferenceMaterialValue;
  index: number;
  onUpdate: (idx: number, patch: Partial<ReferenceMaterialValue>) => void;
  onDelete?: (row: ReferenceMaterialValue, idx: number) => void;
}

const ValueRow: React.FC<ValueRowProps> = React.memo(({ row: r, index: idx, onUpdate, onDelete }) => (
  <TableRow>
    <TableCell>
      <TextField
        size="small"
        value={r.analyte_symbol}
        onChange={e => onUpdate(idx, { analyte_symbol: e.target.value })}
        sx={{ width: 80 }}
      />
    </TableCell>
    <TableCell>
      <TextField
        size="small"
        value={r.analyte_name || ''}
        onChange={e => onUpdate(idx, { analyte_name: e.target.value })}
      />
    </TableCell>
    <TableCell>
      <TextField
        size="small"
        value={r.unit}
        onChange={e => onUpdate(idx, { unit: e.target.value })}
        sx={{ width: 90 }}
      />
    </TableCell>
    <TableCell>
      <TextField
        size="small"
        value={r.method}
        onChange={e => onUpdate(idx, { method: e.target.value })}
      />
    </TableCell>
    <TableCell align="right">
      <TextField
        size="small"
        type="number"
        value={r.certified_value ?? ''}
        onChange={e =>
          onUpdate(idx, {
            certified_value: e.target.value === '' ? null : Number(e.target.value),
          })
        }
        sx={{ width: 110 }}
      />
    </TableCell>
    <TableCell align="right">
      <TextField
        size="small"
        type="number"
        value={r.sd_1 ?? ''}
        onChange={e =>
          onUpdate(idx, {
            sd_1: e.target.value === '' ? null : Number(e.target.value),
          })
        }
        sx={{ width: 100 }}
      />
    </TableCell>
    <TableCell align="right">
      <TextField
        size="small"
        type="number"
        value={r.ci_low_95 ?? ''}
        onChange={e =>
          onUpdate(idx, {
            ci_low_95: e.target.value === '' ? null : Number(e.target.value),
          })
        }
        sx={{ width: 100 }}
      />
    </TableCell>
    <TableCell align="right">
      <TextField
        size="small"
        type="number"
        value={r.ci_high_95 ?? ''}
        onChange={e =>
          onUpdate(idx, {
            ci_high_95: e.target.value === '' ? null : Number(e.target.value),
          })
        }
        sx={{ width: 100 }}
      />
    </TableCell>
    <TableCell>
      <Switch
        size="small"
        checked={r.is_indicative ?? false}
        onChange={(_, v) => onUpdate(idx, { is_indicative: v })}
      />
    </TableCell>
    <TableCell>
      {onDelete && (
        <IconButton size="small" onClick={() => onDelete(r, idx)}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      )}
    </TableCell>
  </TableRow>
));
ValueRow.displayName = 'ValueRow';

const ValueRowsEditor: React.FC<ValueRowsEditorProps> = ({ rows, onChange, onDeleteRow }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Keep the latest props in refs so the update/delete callbacks stay
  // referentially stable across renders — required for React.memo on each row
  // to actually skip re-rendering untouched rows.
  const rowsRef = useRef(rows);
  rowsRef.current = rows;
  const deleteRef = useRef(onDeleteRow);
  deleteRef.current = onDeleteRow;

  const updateRow = useCallback(
    (idx: number, patch: Partial<ReferenceMaterialValue>) => {
      onChange(rowsRef.current.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
    },
    [onChange],
  );
  const deleteRow = useCallback(
    (row: ReferenceMaterialValue, idx: number) => deleteRef.current?.(row, idx),
    [],
  );

  // Clamp the page if rows shrink (e.g. after deletions).
  const pageCount = Math.max(1, Math.ceil(rows.length / rowsPerPage));
  useEffect(() => {
    if (page > pageCount - 1) setPage(pageCount - 1);
  }, [page, pageCount]);

  const start = page * rowsPerPage;
  const pageRows = rows.slice(start, start + rowsPerPage);

  return (
    <>
      <TableContainer component={Paper} sx={{ maxHeight: 460 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Symbole</TableCell>
              <TableCell>Nom</TableCell>
              <TableCell>Unité</TableCell>
              <TableCell>Méthode</TableCell>
              <TableCell align="right">Valeur certifiée</TableCell>
              <TableCell align="right">1SD</TableCell>
              <TableCell align="right">CI 95% bas</TableCell>
              <TableCell align="right">CI 95% haut</TableCell>
              <TableCell>Indicatif</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    Aucun analyte enregistré. Cliquez sur « Ajouter une ligne ».
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {pageRows.map((r, i) => {
              const idx = start + i;
              return (
                <ValueRow
                  key={r.id || `new-${idx}`}
                  row={r}
                  index={idx}
                  onUpdate={updateRow}
                  onDelete={onDeleteRow ? deleteRow : undefined}
                />
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={rows.length}
        page={Math.min(page, pageCount - 1)}
        onPageChange={(_, p) => setPage(p)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={e => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[25, 50, 100]}
        labelRowsPerPage="Lignes par page"
      />
    </>
  );
};

export default ReferenceMaterialsManagement;
